import React, { useEffect, useState, Component } from 'react';
import { motion } from 'framer-motion';
import { Check, X, Star, BookOpen, Heart, Activity, Shield, Brain, Lock, Gift, ShieldCheck, Zap, Award, Clock } from 'lucide-react';
import { CTAButton } from '../components/CTAButton';
import { SocialProofWidget } from '../components/SocialProofWidget';
import { TestimonialCard } from '../components/TestimonialCard';
import { AccordionItem } from '../components/Accordion';
// Countdown Timer Component
function CountdownTimer() {
  const [timeLeft, setTimeLeft] = useState({
    minutes: 7,
    seconds: 0
  });
  useEffect(() => {
    const calculateTimeLeft = () => {
      setTimeLeft(prev => {
        if (prev.minutes === 0 && prev.seconds === 0) {
          // Reset to 7 minutes when it reaches 0
          return {
            minutes: 7,
            seconds: 0
          };
        }
        if (prev.seconds === 0) {
          return {
            minutes: prev.minutes - 1,
            seconds: 59
          };
        }
        return {
          minutes: prev.minutes,
          seconds: prev.seconds - 1
        };
      });
    };
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, []);
  return <div className="flex items-center justify-center gap-2 mb-6" data-id="element-26">
      <Clock className="text-red-500" size={24} data-id="element-27" />
      <div className="flex gap-2 text-white" data-id="element-28">
        <div className="bg-red-600/20 border border-red-500/30 rounded-lg px-3 py-2 min-w-[60px]" data-id="element-29">
          <div className="text-2xl md:text-3xl font-bold" data-id="element-30">
            {String(timeLeft.minutes).padStart(2, '0')}
          </div>
          <div className="text-xs text-gray-400" data-id="element-31">MIN</div>
        </div>
        <div className="flex items-center text-2xl font-bold" data-id="element-32">:</div>
        <div className="bg-red-600/20 border border-red-500/30 rounded-lg px-3 py-2 min-w-[60px]" data-id="element-33">
          <div className="text-2xl md:text-3xl font-bold" data-id="element-34">
            {String(timeLeft.seconds).padStart(2, '0')}
          </div>
          <div className="text-xs text-gray-400" data-id="element-35">SEG</div>
        </div>
      </div>
    </div>;
}
// Image Constants
const IMAGES = {
  HERO: 'https://cdn.magicpatterns.com/uploads/aVi3C2ZeJAHDEmExLn3sbN/Sin_titulo_(1920_x_1800_px)44.png',
  BONUS_MEDITATION: 'https://cdn.magicpatterns.com/uploads/46fPRJCL9RMFsVT9M2vTCY/20251221_1704_Meditacion_Relajante_remix_01kd1858qyfc297xz7pb50e3fa_(1).png',
  BONUS_TOOLS: 'https://cdn.magicpatterns.com/uploads/9tWkme6D1oNmJGoPL4p8TN/20251222_0453_Mejora_de_Claridad_remix_01kd2gpce2eghs9srcac6192py.png',
  BONUS_CHANGE: 'https://cdn.magicpatterns.com/uploads/6JkzEb8z7oJCEtYw7jzxWx/20251222_0455_Sostener_el_Cambio_remix_01kd2gvq26e0msartte5z3tvqr.png',
  BONUS_PROTOCOL: 'https://cdn.magicpatterns.com/uploads/7NWtKb7fypwWUGHTXy1Ejr/20251222_0442_Protocolo_Anticulpa_Transparente_remix_01kd2g3rcyepk96cz647va9px1.png',
  BONUS_WELLNESS: 'https://cdn.magicpatterns.com/uploads/dbbGWQEjVsatyqAwJj69Wy/20251221_1703_Fondo_Transparente_Bienestar_remix_01kd1830n3eke8wwzpa47hgdyb.png',
  MERCADO_PAGO: 'https://cdn.magicpatterns.com/uploads/oKcTFFBLXrfUb1i7BfEspy/Mercado_Pago.svg.webp',
  GUARANTEE_BADGE: 'https://cdn.magicpatterns.com/uploads/jypuua5ri47qiuJpAjd21a/ChatGPT_Image_22_dic_2025,_06_28_24.png'
};
const CHECKOUT_LINK = 'https://checkout.ilumente.online/checkouts/cn/hWN6igHduuD3tLnoSopG2WUk/es-ar?_r=AQABzD-arcRvAEpSW89iX76gLVyjZYE4HBtP6A5ReR0X9es&preview_theme_id=183314547000';
export function LandingPage() {
  const scrollToPricing = (e: React.MouseEvent) => {
    e.preventDefault();
    const pricingSection = document.getElementById('pricing-section');
    if (pricingSection) {
      pricingSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-[#FDFBF7] font-sans text-gray-800 overflow-x-hidden" data-id="element-36">
      {/* 1. Top Trust Strip */}
      <div className="bg-[#FFF8F0] py-2 text-center border-b border-orange-100" data-id="element-37">
        <div className="flex items-center justify-center gap-2 text-sm md:text-base font-medium text-gray-700" data-id="element-38">
          <div className="flex text-yellow-400" data-id="element-39">
            {[...Array(5)].map((_, i) => <Star key={i} size={14} fill="currentColor" data-id="element-40" />)}
          </div>
          <span data-id="element-41">
            Más de 3.247 personas ya comenzaron su renovación emocional
          </span>
        </div>
      </div>

      {/* 2. Hero Section */}
      <header className="relative pt-12 pb-20 px-4 md:pt-20 md:pb-32 overflow-hidden" data-id="element-42">
        <div className="max-w-6xl mx-auto" data-id="element-43">
          {/* Mobile Layout: Vertical stack */}
          <div className="md:hidden flex flex-col gap-8 items-center text-center" data-id="element-44">
            {/* 1. Headline */}
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="text-3xl font-extrabold text-gray-900 leading-tight" data-id="element-45">
              Si estás cansada de{' '}
              <span className="text-[#FF7B29]" data-id="element-46">sobrevivir emocionalmente</span>{' '}
              y pelear con tu cabeza todos los días, esto es para vos.
            </motion.h1>

            {/* 2. Description */}
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="text-lg text-gray-600" data-id="element-47">
              Un sistema simple y humano para dejar la culpa, bajar la ansiedad
              y volver a sentir control en solo 30 días.
            </motion.p>

            {/* 3. Mockup Image */}
            <motion.div initial={{
            opacity: 0,
            x: 50
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8
          }} className="relative z-10 w-full" data-id="element-48">
              <div className="absolute inset-0 bg-orange-200 rounded-full filter blur-3xl opacity-20 animate-pulse" data-id="element-49"></div>
              <img src={IMAGES.HERO} alt="Pack de Renovación Mockup" className="relative w-full h-auto drop-shadow-2xl" data-id="element-50" />
            </motion.div>

            {/* 4. Button */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.4
          }} className="w-full" data-id="element-51">
              <button onClick={scrollToPricing} className="bg-[#FF7B29] hover:bg-[#E06A20] text-white font-bold py-4 px-8 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 text-lg flex items-center gap-2 mx-auto" data-id="element-52">
                QUIERO EMPEZAR MI RENOVACIÓN
              </button>
            </motion.div>

            {/* 5. Trust Badges */}
            <div className="flex items-center gap-4 text-sm text-gray-500 font-medium flex-wrap justify-center" data-id="element-53">
              <span className="flex items-center gap-1" data-id="element-54">
                <Zap size={16} className="text-yellow-500 fill-current" data-id="element-55" />{' '}
                Acceso inmediato
              </span>
              <span className="flex items-center gap-1" data-id="element-56">
                <BookOpen size={16} className="text-blue-500" data-id="element-57" /> Producto
                digital
              </span>
              <span className="flex items-center gap-1" data-id="element-58">
                <Lock size={16} className="text-green-500" data-id="element-59" /> Pago seguro
              </span>
            </div>
          </div>

          {/* Desktop Layout: Side by side */}
          <div className="hidden md:grid md:grid-cols-2 gap-12 items-center" data-id="element-60">
            {/* Text and Button - Left side on desktop */}
            <div className="text-left z-10" data-id="element-61">
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-6" data-id="element-62">
                Si estás cansada de{' '}
                <span className="text-[#FF7B29]" data-id="element-63">
                  sobrevivir emocionalmente
                </span>{' '}
                y pelear con tu cabeza todos los días, esto es para vos.
              </motion.h1>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2
            }} className="text-xl text-gray-600 mb-8" data-id="element-64">
                Un sistema simple y humano para dejar la culpa, bajar la
                ansiedad y volver a sentir control en solo 30 días.
              </motion.p>

              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: 0.4
            }} className="flex flex-col items-start gap-4" data-id="element-65">
                <button onClick={scrollToPricing} className="bg-[#FF7B29] hover:bg-[#E06A20] text-white font-bold py-4 px-8 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 text-xl flex items-center gap-2" data-id="element-66">
                  QUIERO EMPEZAR MI RENOVACIÓN
                </button>

                <div className="flex items-center gap-4 text-sm text-gray-500 font-medium" data-id="element-67">
                  <span className="flex items-center gap-1" data-id="element-68">
                    <Zap size={16} className="text-yellow-500 fill-current" data-id="element-69" />{' '}
                    Acceso inmediato
                  </span>
                  <span className="flex items-center gap-1" data-id="element-70">
                    <BookOpen size={16} className="text-blue-500" data-id="element-71" /> Producto
                    digital
                  </span>
                  <span className="flex items-center gap-1" data-id="element-72">
                    <Lock size={16} className="text-green-500" data-id="element-73" /> Pago seguro
                  </span>
                </div>
              </motion.div>
            </div>

            {/* Image - Right side on desktop */}
            <motion.div initial={{
            opacity: 0,
            x: 50
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8
          }} className="relative z-10" data-id="element-74">
              <div className="absolute inset-0 bg-orange-200 rounded-full filter blur-3xl opacity-20 animate-pulse" data-id="element-75"></div>
              <img src={IMAGES.HERO} alt="Pack de Renovación Mockup" className="relative w-full h-auto drop-shadow-2xl transform hover:scale-105 transition-transform duration-500" data-id="element-76" />
            </motion.div>
          </div>
        </div>
      </header>

      {/* 3. Floating Social Proof */}
      <SocialProofWidget data-id="element-77" />

      {/* 4. Pain Identification */}
      <section className="py-20 px-4 bg-white" data-id="element-78">
        <div className="max-w-5xl mx-auto" data-id="element-79">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900" data-id="element-80">
            ¿Te sentís así últimamente?
          </h2>
          <div className="grid md:grid-cols-2 gap-6 mb-12" data-id="element-81">
            {['Vivís cansada mentalmente y no sabés cómo parar', 'Te castigás por cada decisión que tomás', 'La culpa te paraliza y no te deja avanzar', 'Sentís que nunca hacés las cosas lo suficientemente bien', 'Evitás mirar tus números o enfrentar tu realidad', 'Te sentís atrapada en un ciclo de autosabotaje', 'La ansiedad te consume y no podés relajarte', 'Sentís que siempre fallás, aunque te esfuerces al máximo', 'Te culpás por cosas que no podés controlar', 'Tenés miedo de tomar decisiones por si te equivocás'].map((pain, index) => <motion.div key={index} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.05
          }} className="flex items-center gap-4 p-4 bg-red-50 rounded-xl border border-red-100" data-id="element-82">
                <div className="bg-red-100 p-2 rounded-full flex-shrink-0" data-id="element-83">
                  <X className="text-red-500 w-6 h-6" data-id="element-84" />
                </div>
                <span className="text-base md:text-lg text-gray-800 font-medium" data-id="element-85">
                  {pain}
                </span>
              </motion.div>)}
          </div>
          <div className="text-center bg-orange-50 p-6 rounded-2xl border border-orange-100" data-id="element-86">
            <p className="text-xl md:text-2xl font-bold text-[#FF7B29]" data-id="element-87">
              👉 No sos irresponsable. 👉 Estás sobreviviendo.
            </p>
          </div>
        </div>
      </section>

      {/* 5. What is it */}
      <section className="py-20 px-4 bg-[#FDFBF7]" data-id="element-88">
        <div className="max-w-4xl mx-auto text-center" data-id="element-89">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-gray-900" data-id="element-90">
            ¿Qué es <span className="text-[#48C579]" data-id="element-91">Pack de Renovación</span>?
          </h2>
          <p className="text-xl md:text-2xl text-gray-600 leading-relaxed" data-id="element-92">
            Pack de Renovación es un sistema práctico diseñado para{' '}
            <span className="font-bold text-[#48C579] bg-green-50 px-2 py-1 rounded" data-id="element-93">
              personas reales
            </span>{' '}
            que están cansadas de sobrevivir emocionalmente y quieren{' '}
            <span className="font-bold text-[#48C579] bg-green-50 px-2 py-1 rounded" data-id="element-94">
              empezar a sentirse mejor
            </span>{' '}
            sin exigencias imposibles.
          </p>
        </div>
      </section>

      {/* 6. What's Included (Core System) */}
      <section className="py-20 px-4 bg-white" data-id="element-95">
        <div className="max-w-6xl mx-auto" data-id="element-96">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 text-gray-900" data-id="element-97">
            Lo que incluye el sistema
          </h2>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-98">
            {[{
            icon: BookOpen,
            title: 'Ebook Pack de Renovación',
            subtitle: '+200 páginas de contenido transformador'
          }, {
            icon: Brain,
            title: 'Guía práctica',
            subtitle: 'Herramientas emocionales aplicables'
          }, {
            icon: Activity,
            title: 'Planilla de seguimiento',
            subtitle: 'Para medir tu progreso real'
          }].map((item, i) => <motion.div key={i} whileHover={{
            y: -10
          }} className="bg-gray-50 p-8 rounded-2xl text-center border border-gray-100 shadow-sm" data-id="element-99">
                <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 text-[#FF7B29]" data-id="element-100">
                  <item.icon size={32} data-id="element-101" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-gray-900" data-id="element-102">
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-103">{item.subtitle}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 7. Bonuses Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-green-50 via-green-50/30 to-white relative overflow-hidden" data-id="element-104">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-105">
          <div className="absolute top-10 left-10 w-64 h-64 bg-green-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob" data-id="element-106"></div>
          <div className="absolute top-10 right-10 w-64 h-64 bg-emerald-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000" data-id="element-107"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10" data-id="element-108">
          <div className="text-center mb-16" data-id="element-109">
            <span className="inline-block py-1 px-3 rounded-full bg-green-100 text-green-800 font-bold text-sm tracking-wide mb-4 border border-green-200" data-id="element-110">
              OFERTA LIMITADA
            </span>
            <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-6" data-id="element-111">
              Regalos Exclusivos <span className="text-green-600" data-id="element-112">GRATIS</span>{' '}
              si accedés hoy
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-113">
              Llevate este paquete de herramientas adicionales valorado en más
              de $20.000, totalmente bonificado con tu compra.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16 justify-center" data-id="element-114">
            {[{
            img: IMAGES.BONUS_MEDITATION,
            title: 'Bonus 1: Meditación guiada',
            desc: 'Para calmar la mente y bajar ansiedad inmediata.',
            value: '$4.500'
          }, {
            img: IMAGES.BONUS_TOOLS,
            title: 'Bonus 2: Guía de Herramientas',
            desc: 'Recursos prácticos para aplicar en tu día a día.',
            value: '$3.800'
          }, {
            img: IMAGES.BONUS_CHANGE,
            title: 'Bonus 3: Sostener el cambio',
            desc: 'Guía para no volver al autosabotaje emocional.',
            value: '$5.200'
          }, {
            img: IMAGES.BONUS_PROTOCOL,
            title: 'Bonus 4: Protocolo Anticulpa',
            desc: 'Paso a paso para detener el castigo mental.',
            value: '$4.000'
          }, {
            img: IMAGES.BONUS_WELLNESS,
            title: 'Bonus 5: Rutinas de Bienestar',
            desc: 'Rutinas simples para mañana, noche y días difíciles.',
            value: '$3.500'
          }].map((bonus, i) => <motion.div key={i} whileHover={{
            y: -10
          }} className={`bg-white rounded-2xl overflow-hidden shadow-xl border-2 border-green-400 relative flex flex-col ${i === 3 || i === 4 ? 'lg:col-span-1 md:col-span-1' : ''}`} data-id="element-115">
                <div className="absolute top-4 right-4 z-20 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg flex items-center gap-1" data-id="element-116">
                  <Gift size={12} data-id="element-117" /> GRATIS HOY
                </div>

                <div className="h-64 md:h-72 overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 relative group" data-id="element-118">
                  <img src={bonus.img} alt={bonus.title} className="w-full h-full object-contain p-4 transform group-hover:scale-110 transition-transform duration-700" data-id="element-119" />
                  <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-120"></div>
                </div>

                <div className="p-6 flex-1 flex flex-col" data-id="element-121">
                  <h3 className="font-bold text-xl mb-2 text-gray-900" data-id="element-122">
                    {bonus.title}
                  </h3>
                  <p className="text-gray-600 mb-4 flex-1" data-id="element-123">{bonus.desc}</p>
                  <div className="pt-4 border-t border-gray-100 flex justify-between items-center" data-id="element-124">
                    <span className="text-gray-400 line-through text-sm" data-id="element-125">
                      Valor: {bonus.value}
                    </span>
                    <span className="text-green-600 font-bold text-sm bg-green-50 px-2 py-1 rounded" data-id="element-126">
                      ¡BONIFICADO!
                    </span>
                  </div>
                </div>
              </motion.div>)}
          </div>

          <div className="text-center" data-id="element-127">
            {/* SCROLL TO PRICING BUTTON */}
            <button onClick={scrollToPricing} className="bg-[#FF7B29] hover:bg-[#E06A20] text-white font-bold py-4 px-8 rounded-full shadow-green-200 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 text-lg md:text-xl" data-id="element-128">
              QUIERO TODOS LOS BONUS GRATIS
            </button>
            <p className="mt-4 text-sm text-gray-500" data-id="element-129">
              * Oferta válida solo por tiempo limitado
            </p>
          </div>
        </div>
      </section>

      {/* 8. Testimonials */}
      <section className="py-20 px-4 bg-white" data-id="element-130">
        <div className="max-w-6xl mx-auto" data-id="element-131">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 text-gray-900" data-id="element-132">
            Historias reales de renovación
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-133">
            {[{
            name: 'María L.',
            quote: 'Dejé de castigarme por cada decisión. Ahora puedo respirar.',
            img: 10
          }, {
            name: 'Carolina P.',
            quote: 'Por primera vez en años, siento que no estoy fallando todo el tiempo.',
            img: 25
          }, {
            name: 'Sofía M.',
            quote: 'La culpa ya no me paraliza. Tengo herramientas reales.',
            img: 32
          }, {
            name: 'Laura R.',
            quote: 'Empecé a confiar en mí de nuevo. Esto cambió todo.',
            img: 44
          }, {
            name: 'Daniela G.',
            quote: 'No sabía que podía sentirme así de liviana emocionalmente.',
            img: 5
          }, {
            name: 'Valentina S.',
            quote: 'Las rutinas de bienestar son oro puro. Simples pero poderosas.',
            img: 12
          }, {
            name: 'Camila R.',
            quote: 'Me ayudó a ordenar mi cabeza cuando sentía que explotaba.',
            img: 18
          }, {
            name: 'Andrea M.',
            quote: 'La mejor inversión que hice en mi salud mental este año.',
            img: 29
          }, {
            name: 'Patricia B.',
            quote: 'Gracias por explicarlo tan simple. Me siento mucho mejor.',
            img: 36
          }].map((t, i) => <TestimonialCard key={i} name={t.name} quote={t.quote} imageIndex={t.img} data-id="element-134" />)}
          </div>
        </div>
      </section>

      {/* 10. Who this is / isn't for */}
      <section className="py-20 px-4 bg-[#FDFBF7]" data-id="element-135">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8" data-id="element-136">
          <div className="bg-green-50 p-8 rounded-3xl border border-green-100" data-id="element-137">
            <h3 className="text-2xl font-bold mb-6 text-gray-900 flex items-center gap-2" data-id="element-138">
              <Check className="text-green-600" data-id="element-139" /> Esto es para vos si:
            </h3>
            <ul className="space-y-4" data-id="element-140">
              {['Estás cansada mentalmente', 'Querés alivio real', 'Querés empezar sin exigirte perfección', 'Buscás herramientas prácticas y aplicables', 'Estás lista para hacer un cambio', 'Valorás tu bienestar emocional'].map((item, i) => <li key={i} className="flex items-center gap-3 text-gray-700" data-id="element-141">
                  <div className="w-6 h-6 rounded-full bg-green-200 flex items-center justify-center flex-shrink-0" data-id="element-142">
                    <Check size={14} className="text-green-700" data-id="element-143" />
                  </div>
                  {item}
                </li>)}
            </ul>
          </div>

          <div className="bg-red-50 p-8 rounded-3xl border border-red-100" data-id="element-144">
            <h3 className="text-2xl font-bold mb-6 text-gray-900 flex items-center gap-2" data-id="element-145">
              <X className="text-red-500" data-id="element-146" /> Esto NO es para vos si:
            </h3>
            <ul className="space-y-4" data-id="element-147">
              {['Buscás soluciones mágicas', 'No querés mirarte', 'Esperás que alguien más lo haga por vos', 'No estás dispuesta a dedicarle tiempo', 'Querés resultados sin compromiso', 'Preferís seguir como estás'].map((item, i) => <li key={i} className="flex items-center gap-3 text-gray-700" data-id="element-148">
                  <div className="w-6 h-6 rounded-full bg-red-200 flex items-center justify-center flex-shrink-0" data-id="element-149">
                    <X size={14} className="text-red-700" data-id="element-150" />
                  </div>
                  {item}
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* 11. Final Price & Decision Block */}
      <section id="pricing-section" className="py-24 px-4 bg-[#0a0a0a] text-white text-center relative overflow-hidden" data-id="element-151">
        <div className="absolute top-0 right-0 w-96 h-96 bg-green-900/20 rounded-full blur-3xl" data-id="element-152"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-900/20 rounded-full blur-3xl" data-id="element-153"></div>

        <div className="max-w-4xl mx-auto relative z-10" data-id="element-154">
          <h2 className="text-3xl md:text-5xl font-bold mb-8 leading-tight text-white" data-id="element-155">
            No necesitás cambiar <span className="text-[#7ED321]" data-id="element-156">tu vida</span>
            . <br data-id="element-157" />
            Solo necesitás empezar.
          </h2>

          <div className="bg-gray-900/80 backdrop-blur-md rounded-3xl p-8 md:p-12 max-w-2xl mx-auto border border-gray-800 shadow-2xl ring-1 ring-white/10" data-id="element-158">
            {/* Discount Badge */}
            <div className="mb-6" data-id="element-159">
              <div className="inline-block bg-gradient-to-r from-red-600 to-red-500 text-white px-6 py-3 rounded-full text-2xl md:text-3xl font-black shadow-lg animate-pulse" data-id="element-160">
                90% DE DESCUENTO
              </div>
              <p className="text-yellow-400 font-bold text-lg mt-2" data-id="element-161">
                Solo por hoy
              </p>
            </div>

            <CountdownTimer data-id="element-162" />

            <div className="flex flex-col items-center justify-center mb-8 mt-6" data-id="element-163">
              <p className="text-gray-400 text-lg font-medium mb-2" data-id="element-164">
                ANTES:{' '}
                <span className="text-red-500 line-through" data-id="element-165">$49.990</span>
              </p>
              <div className="flex items-baseline gap-2" data-id="element-166">
                <span className="text-5xl md:text-7xl font-extrabold text-[#7ED321] tracking-tight" data-id="element-167">
                  $4.999
                </span>
                <span className="text-xl text-gray-400" data-id="element-168">ARS</span>
              </div>
              <p className="text-sm text-green-400 mt-2 font-medium bg-green-900/30 px-3 py-1 rounded-full border border-green-800" data-id="element-169">
                Ahorrás $44.991 hoy
              </p>
            </div>

            {/* DIRECT CHECKOUT LINK */}
            <CTAButton text="EMPEZAR MI RENOVACIÓN AHORA" link={CHECKOUT_LINK} variant="apple" size="xl" pulse={true} className="w-full shadow-lg shadow-lime-900/50 hover:shadow-lime-900/80 transition-shadow" data-id="element-170" />

            {/* Trust Indicators */}
            <div className="mt-8 pt-8 border-t border-gray-800" data-id="element-171">
              <div className="flex flex-col items-center justify-center gap-6 text-sm text-gray-400" data-id="element-172">
                <div className="flex items-center gap-2" data-id="element-173">
                  <Lock size={16} className="text-green-500" data-id="element-174" />
                  <span data-id="element-175">Pago 100% Seguro con Mercado Pago</span>
                </div>

                {/* Mercado Pago Logo */}
                <div className="w-full max-w-xs mx-auto" data-id="element-176">
                  <img src={IMAGES.MERCADO_PAGO} alt="Mercado Pago" className="w-full h-auto opacity-90" data-id="element-177" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NEW SECTION: Money Back Guarantee */}
      <section className="py-16 px-4 bg-gradient-to-b from-green-50 to-white border-t border-green-100" data-id="element-178">
        <div className="max-w-5xl mx-auto" data-id="element-179">
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12 bg-white rounded-3xl p-8 md:p-12 shadow-lg" data-id="element-180">
            <div className="flex-shrink-0" data-id="element-181">
              <img src={IMAGES.GUARANTEE_BADGE} alt="Garantía 7 días" className="w-48 h-48 md:w-56 md:h-56 object-contain" data-id="element-182" />
            </div>
            <div className="text-center md:text-left flex-1" data-id="element-183">
              <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-184">
                Garantía sin vueltas
              </h3>
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-4" data-id="element-185">
                Estamos tan seguros de que este Programa te va a encantar, que
                si no es así, te devolvemos el 100% de tu dinero.
              </p>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed" data-id="element-186">
                Tenés 7 días para probarlo sin riesgo. Si no te convence, nos
                escribís y te hacemos el reembolso. Así de simple.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 9. FAQ */}
      <section className="py-20 px-4 bg-[#FDFBF7]" data-id="element-187">
        <div className="max-w-3xl mx-auto" data-id="element-188">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900" data-id="element-189">
            Preguntas Frecuentes
          </h2>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8" data-id="element-190">
            <AccordionItem question="¿Para quién es este pack?" answer="Es para cualquier persona que sienta carga mental, culpa recurrente o ansiedad por el futuro y quiera herramientas prácticas para gestionarlo." data-id="element-191" />
            <AccordionItem question="¿Necesito experiencia previa o terapia?" answer="No, el material está diseñado con un lenguaje simple y accesible. No reemplaza la terapia, pero es un excelente complemento o punto de partida." data-id="element-192" />
            <AccordionItem question="¿Cuánto tiempo lleva por día?" answer="El sistema es flexible. Podés dedicarle desde 15 minutos al día. No hay presiones ni fechas límite." data-id="element-193" />
            <AccordionItem question="¿Cómo accedo al contenido?" answer="Inmediatamente después de tu compra recibís un correo con el acceso a todo el material digital para descargar o ver online." data-id="element-194" />
            <AccordionItem question="¿Es un pago único?" answer="Sí, es un único pago. No hay suscripciones ni cobros mensuales sorpresa." data-id="element-195" />
          </div>
        </div>
      </section>

      {/* 12. Final Trust Strip */}
      <div className="bg-gray-900 py-8 text-center text-gray-400 text-sm border-t border-gray-800" data-id="element-196">
        <div className="flex flex-wrap justify-center gap-6 mb-4" data-id="element-197">
          <span className="flex items-center gap-2" data-id="element-198">
            <ShieldCheck size={16} className="text-green-500" data-id="element-199" /> Garantía de 7
            días
          </span>
          <span className="flex items-center gap-2" data-id="element-200">
            <Zap size={16} className="text-yellow-500" data-id="element-201" /> Acceso inmediato
          </span>
          <span className="flex items-center gap-2" data-id="element-202">
            <Lock size={16} className="text-blue-500" data-id="element-203" /> Compra 100% segura
          </span>
        </div>
        <div className="opacity-50" data-id="element-204">
          © {new Date().getFullYear()} Pack de Renovación. Todos los derechos
          reservados.
        </div>
      </div>
    </div>;
}